import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { useAuth } from '../hooks/useAuth';
import { deviceLogsApi, DeviceLog } from '../api/deviceLogs.api';
import { toast } from '../utils/toast';

const LIMIT_OPTIONS = [25, 50, 100, 200];

function formatTimestamp(ts: string) {
  return new Date(ts).toLocaleString();
}

function IgnitionBadge({ value }: { value: number | null }) {
  if (value === null) return <span className="text-gray-400">—</span>;
  return value === 1
    ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">ON</span>
    : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">OFF</span>;
}

function MovementBadge({ value }: { value: number | null }) {
  if (value === null) return <span className="text-gray-400">—</span>;
  return value === 1
    ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Moving</span>
    : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">Parked</span>;
}

function ActionBadge({ value }: { value: string | null }) {
  if (!value) return <span className="text-gray-400">—</span>;
  const colors: Record<string, string> = {
    trip_started:     'bg-green-100 text-green-800',
    trip_ended:       'bg-red-100 text-red-800',
    trip_in_progress: 'bg-yellow-100 text-yellow-800',
    record_stored:    'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[value] ?? 'bg-gray-100 text-gray-600'}`}>
      {value.replace(/_/g, ' ')}
    </span>
  );
}

export const DeviceLogsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isSuperAdmin = user?.isSuperAdmin === true || user?.role === 'super_admin';

  const [logs, setLogs] = useState<DeviceLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);

  // Filters
  const [imei, setImei]   = useState('');
  const [from, setFrom]   = useState('');
  const [to, setTo]       = useState('');
  const [page, setPage]   = useState(1);
  const [limit, setLimit] = useState(50);

  // Auto-refresh
  const [autoRefresh, setAutoRefresh]   = useState(false);
  const autoRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null);

  if (!isSuperAdmin) return <Navigate to="/dashboard" replace />;

  const fetchLogs = useCallback(async (p = page) => {
    setIsLoading(true);
    try {
      const data = await deviceLogsApi.getLogs({
        imei:  imei.trim() || undefined,
        from:  from || undefined,
        to:    to   || undefined,
        page:  p,
        limit,
      });
      setLogs(data.logs);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('deviceLogs.fetchError'));
    } finally {
      setIsLoading(false);
    }
  }, [imei, from, to, page, limit, t]);

  // Initial load
  useEffect(() => { fetchLogs(1); setPage(1); }, []);

  // Auto-refresh every 10s
  useEffect(() => {
    if (autoRefresh) {
      autoRefreshRef.current = setInterval(() => fetchLogs(page), 10000);
    } else {
      if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
    }
    return () => { if (autoRefreshRef.current) clearInterval(autoRefreshRef.current); };
  }, [autoRefresh, fetchLogs, page]);

  const handleSearch = () => { setPage(1); fetchLogs(1); };
  const handlePageChange = (p: number) => { setPage(p); fetchLogs(p); };

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('deviceLogs.title')}</h1>
            <p className="text-sm text-gray-500 mt-1">{t('deviceLogs.subtitle')}</p>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
              <div
                onClick={() => setAutoRefresh(v => !v)}
                className={`relative w-10 h-5 rounded-full transition-colors ${autoRefresh ? 'bg-green-500' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${autoRefresh ? 'translate-x-5' : ''}`} />
              </div>
              {t('deviceLogs.autoRefresh')}
            </label>
            <Button variant="outline" size="sm" onClick={() => fetchLogs(page)} disabled={isLoading}>
              {isLoading ? t('common.loading') : t('deviceLogs.refresh')}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Input
              label={t('deviceLogs.filterImei')}
              placeholder="862272085372375"
              value={imei}
              onChange={e => setImei(e.target.value.replace(/\D/g, '').slice(0, 15))}
              inputMode="numeric"
              maxLength={15}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('deviceLogs.filterFrom')}</label>
              <input
                type="datetime-local"
                value={from}
                onChange={e => setFrom(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('deviceLogs.filterTo')}</label>
              <input
                type="datetime-local"
                value={to}
                onChange={e => setTo(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('deviceLogs.perPage')}</label>
              <select
                value={limit}
                onChange={e => setLimit(Number(e.target.value))}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {LIMIT_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button onClick={handleSearch} disabled={isLoading}>
              {t('deviceLogs.search')}
            </Button>
          </div>
        </div>

        {/* Results info */}
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>{t('deviceLogs.totalResults', { count: total })}</span>
          {autoRefresh && (
            <span className="flex items-center gap-1 text-green-600">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              {t('deviceLogs.liveMode')}
            </span>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['timestamp','imei','gps','speed','alt','sat','ignition','movement','odometer','action'].map(col => (
                    <th key={col} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      {t(`deviceLogs.col.${col}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-8 text-center text-gray-400">
                      {t('common.loading')}
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-8 text-center text-gray-400">
                      {t('deviceLogs.noLogs')}
                    </td>
                  </tr>
                ) : logs.map(log => (
                  <tr key={log._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap font-mono">
                      {formatTimestamp(log.timestamp)}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-900 whitespace-nowrap">
                      {log.imei}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap font-mono">
                      {log.lat != null && log.lon != null
                        ? `${log.lat.toFixed(5)}, ${log.lon.toFixed(5)}`
                        : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap">
                      {log.speed != null ? `${log.speed} km/h` : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap">
                      {log.altitude != null ? `${log.altitude} m` : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap">
                      {log.satellites ?? <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <IgnitionBadge value={log.ignition} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <MovementBadge value={log.movement} />
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap">
                      {log.odometer != null ? `${log.odometer.toLocaleString()} km` : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <ActionBadge value={log.action} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <span className="text-sm text-gray-500">
                {t('deviceLogs.page', { page, pages })}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handlePageChange(page - 1)} disabled={page <= 1 || isLoading}>
                  {t('common.previous')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => handlePageChange(page + 1)} disabled={page >= pages || isLoading}>
                  {t('common.next')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};
